// Transactions.js
import { useState, useEffect, useCallback } from "react";
import { getTransactions, exportTransactionsCsv } from "../../service/Mainservice";
import ExportCsvButton from "../ExportCsvButton";
import Loader from "../../Loader/Loader";
import Pagination from "../Pagination/pagination"; // adjust path if needed
import ListControls from "../ListControls/ListControls";
import "./Transactions.css";

// MUI Icons
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ClearIcon from "@mui/icons-material/Clear";

const Transactions = () => {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    type: "all",
    startDate: new Date(new Date().setDate(1)).toISOString().split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
  });
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");

  // Summary stats
  const totalTransactions = transactions?.length;
  const totalAmount = transactions?.reduce(
    (sum, t) => sum + (t.amount || 0),
    0,
  );
  const typeCounts = transactions?.reduce((acc, t) => {
    acc[t.type] = (acc[t.type] || 0) + 1;
    return acc;
  }, {});

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch data when filters or debounced search or page changes
  const fetchData = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = {
          ...filters,
          search: debouncedSearch,
          page,
          pageSize: 20,
          sortBy,
          sortOrder,
        };
        const res = await getTransactions(params);
        setTransactions(res.data || []);
        setPagination(
          res.pagination || {
            page: 1,
            pageSize: 20,
            totalCount: 0,
            totalPages: 0,
          },
        );
      } catch (err) {
        console.error("Error fetching transactions:", err);
      } finally {
        setLoading(false);
      }
    },
    [filters, debouncedSearch, sortBy, sortOrder],
  );

  useEffect(() => {
    fetchData(1);
  }, [fetchData]); // runs on filter/search change

  const handlePageChange = (newPage) => {
    fetchData(newPage);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      type: "all",
      startDate: new Date(new Date().setDate(1)).toISOString().split("T")[0],
      endDate: new Date().toISOString().split("T")[0],
    });
    setSearch("");
    setDebouncedSearch("");
  };

  // Helper: status badge class
  const getStatusClass = (status) => {
    const map = {
      completed: "status-completed",
      success: "status-completed",
      failed: "status-failed",
      pending: "status-pending",
      refunded: "status-refunded",
      cancelled: "status-cancelled",
    };
    return map[status?.toLowerCase()] || "";
  };

  // Helper: type badge
  const getTypeClass = (type) => {
    const map = {
      order: "type-order",
      refund: "type-refund",
      payout: "type-payout",
      fee: "type-fee",
    };
    return map[type?.toLowerCase()] || "";
  };

  return (
    <div className="transactions-page">
      <ListControls sortOptions={[{value:"date",label:"Date"},{value:"amount",label:"Amount"},{value:"type",label:"Type"},{value:"referenceId",label:"Reference"},{value:"entityName",label:"Entity"}]} sortBy={sortBy} sortOrder={sortOrder} onSortByChange={setSortBy} onSortOrderChange={setSortOrder} />
      <ExportCsvButton exportRequest={exportTransactionsCsv} filters={{ ...filters, search: debouncedSearch, sortBy, sortOrder }} filename="transactions.csv" />
      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-icon" style={{ color: "#133a34" }}>
            <ReceiptIcon />
          </div>
          <div className="summary-content">
            <span className="summary-label">Total Transactions</span>
            <span className="summary-value">{totalTransactions}</span>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon" style={{ color: "#eb6718" }}>
            <AttachMoneyIcon />
          </div>
          <div className="summary-content">
            <span className="summary-label">Total Amount</span>
            <span className="summary-value">${totalAmount.toFixed(2)}</span>
          </div>
        </div>
        {Object.entries(typeCounts).map(([type, count]) => (
          <div key={type} className="summary-card summary-card-small">
            <div className="summary-icon" style={{ color: "#2563EB" }}>
              <AttachMoneyIcon />
            </div>{" "}
            <div className="summary-content">
              <span className="summary-label">
                {type.charAt(0).toUpperCase() + type.slice(1)}s
              </span>
              <span className="summary-value">{count}</span>
            </div>
          </div>
        ))}
        {Object.keys(typeCounts).length === 0 && (
          <div className="summary-card">
            <div className="summary-content">
              <span className="summary-label">No data</span>
              <span className="summary-value">-</span>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="filters-container">
        <div className="filters-row">
          <div className="filter-group">
            <label>
              <FilterListIcon fontSize="small" /> Type
            </label>
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange("type", e.target.value)}
            >
              <option value="all">All</option>
              <option value="order">Orders</option>
              <option value="refund">Refunds</option>
              <option value="payout">Payouts</option>
              <option value="fee">Fees</option>
            </select>
          </div>
          <div className="filter-group">
            <label>
              <CalendarTodayIcon fontSize="small" /> From
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange("startDate", e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>
              <CalendarTodayIcon fontSize="small" /> To
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange("endDate", e.target.value)}
            />
          </div>
          <div className="filter-group search-group">
            <SearchIcon className="search-icon" />
            <input
              type="text"
              placeholder="Search by ID, description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
            {search && (
              <button className="clear-search" onClick={() => setSearch("")}>
                <ClearIcon fontSize="small" />
              </button>
            )}
          </div>
          <button className="btn-secondary" onClick={clearFilters}>
            Clear Filters
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="table-wrapper">
            <table className="data_table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      style={{ textAlign: "center", padding: "30px 0" }}
                    >
                      No transactions found.
                    </td>
                  </tr>
                ) : (
                  transactions.map((t) => (
                    <tr key={t.id}>
                      <td className="truncate-id">{t.id}</td>
                      <td>
                        <span className={`type-badge ${getTypeClass(t.type)}`}>
                          {t.type || "N/A"}
                        </span>
                      </td>
                      <td>${t.amount?.toFixed(2) || "0.00"}</td>
                      <td>
                        <span
                          className={`status-badge ${getStatusClass(t.status)}`}
                        >
                          {t.status || "N/A"}
                        </span>
                      </td>
                      <td>
                        {t.date ? new Date(t.date).toLocaleDateString() : "N/A"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <Pagination pagedata={pagination} onPageChange={handlePageChange} />
          )}
        </>
      )}
    </div>
  );
};

export default Transactions;
